import { useFormContext, Controller } from 'react-hook-form';
import {
  FormControl,
  FormControlProps,
  Tabs,
  Tab,
  Divider,
  Popper,
  Grow,
  ClickAwayListener,
  Paper,
  IconButton,
} from 'material-hu';
import { Icon, IconTypes } from 'src/types/icons';
import { useRef, useState } from 'react';
import { ArrowRight } from 'material-hu/icons';
import Picker from '../dashboard/reactions/Picker';
import { useQuery } from 'react-query';
import { getPublicIcons } from 'src/services/icons';
import CustomIcon from 'src/components/CustomIcon';
import FormImageSelect from './FormImageSelect';
import { formInputKeys } from './queries';
import { useTranslation } from './i18n';

type Props = FormControlProps & {
  name: string;
  width?: number;
  selectorHandler?: (icon: { value: string; type: IconTypes }) => void;
  defaultIcon?: Icon;
  disabled?: boolean;
  withArrow?: boolean;
};

function FormImageMenu({
  name,
  width = 600,
  selectorHandler,
  defaultIcon = null,
  disabled = false,
  withArrow = true,
  ...formControlProps
}: Props) {
  const { control, setValue } = useFormContext();
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState(0);
  const [open, setOpen] = useState(false);

  const anchorRef = useRef(null);
  const imageSelectRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery(
    formInputKeys.publicIcons(),
    () => getPublicIcons(),
    {
      select: res => res?.data,
    },
  );

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = event => {
    if (anchorRef.current?.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const onEmojiClick = emojiObject => {
    setValue(name, { value: emojiObject.emoji, type: IconTypes.EMOJI });
    if (selectorHandler)
      selectorHandler({ value: emojiObject.emoji, type: IconTypes.EMOJI });
    setOpen(false);
  };

  const formImageSelect = (
    <FormImageSelect
      name={name}
      options={data}
      closePopup={handleClose}
      selectorHandler={selectorHandler}
      ref={imageSelectRef}
    />
  );

  const emojis = (
    <Picker
      height={270}
      onEmojiClick={onEmojiClick}
    />
  );

  const tabs = [
    { label: t('ICONS'), component: formImageSelect },
    { label: t('EMOJIS'), component: emojis },
  ];

  const { component } = tabs[currentTab];

  return (
    <FormControl
      {...formControlProps}
      sx={{
        display: 'block',
        ...formControlProps?.sx,
      }}
    >
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <>
            <IconButton
              disabled={disabled}
              onClick={handleToggle}
              ref={anchorRef}
            >
              <CustomIcon
                icon={!defaultIcon ? field.value : defaultIcon}
                size={defaultIcon ? 35 : 50}
              />
              {withArrow && <ArrowRight fontSize="large" />}
            </IconButton>
            <ClickAwayListener onClickAway={handleClose}>
              <Popper
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                placement="right-start"
                sx={{ zIndex: 10000 }}
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin:
                        placement === 'bottom' ? 'center top' : 'center bottom',
                    }}
                  >
                    <Paper sx={{ width: { width } }}>
                      <Tabs
                        value={currentTab}
                        onChange={(_, val) => {
                          setCurrentTab(val);
                        }}
                        variant="scrollable"
                      >
                        {tabs.map(tab => (
                          <Tab
                            sx={{ p: 1 }}
                            key={tab.label}
                            label={tab.label}
                          />
                        ))}
                      </Tabs>
                      <Divider sx={{ mb: 2 }} />
                      {component}
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </ClickAwayListener>
          </>
        )}
      />
    </FormControl>
  );
}

export default FormImageMenu;
